import { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Element } from 'react-scroll';
import { Link, useLocation } from 'react-router-dom';
import { Projects } from './Projects';
import { Blog } from './Blog';
import { ContactSophisticated } from './ContactSophisticated';
import { AnimatedOrbs } from '../components/AnimatedOrbs';

interface DynamicText {
  text: string;
  color: string;
}

const dynamicTexts: DynamicText[] = [
  { text: "Builder", color: "text-blue-500" },
  { text: "Tech Enthusiast", color: "text-green-500" },
  { text: "Founder", color: "text-purple-500" },
  { text: "Blogger", color: "text-indigo-500" },
  { text: "Market Observer", color: "text-orange-500" },
  { text: "Foodie", color: "text-emerald-500" }
];

export function HomeSophisticated() {
  const [textIndex, setTextIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { scrollY } = useScroll();
  
  // Parallax transforms
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 400], [1, 0.95]);

  // Cycle through dynamic text
  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % dynamicTexts.length);
    }, 4000); // Increased to 4 seconds for better readability
    return () => clearInterval(interval);
  }, []);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for scroll to bottom instruction from navigation state
  useEffect(() => {
    if (location.state?.scrollToBottom) {
      // Wait for page to fully render, then scroll to bottom
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 500);
    }
  }, [location.state]);

  return (
    <div ref={containerRef} className="relative">
      {/* Progress Indicator */}
      <div className="fixed top-0 left-0 w-1 h-full bg-gray-100 z-40">
        <div 
          className="bg-gradient-to-b from-orange-500 to-green-500 w-full transition-all duration-300"
          style={{ height: `${scrollProgress}%` }}
        />
      </div>

      {/* Hero Section */}
      <Element name="home">
        <motion.section 
          className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800"
          style={{ 
            opacity: heroOpacity, 
            scale: heroScale
          }}
        >
          {/* Floating Gradient Orbs */}
          <AnimatedOrbs />
          
          {/* Main content */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
            <div className="space-y-8">
              {/* Animated greeting */}
              <div className="overflow-hidden">
                <motion.h1 
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="text-7xl md:text-8xl font-light tracking-tight text-gray-900 dark:text-white"
                >
                  Hi I'm Sean
                </motion.h1>
              </div>
              
              {/* Dynamic subtitle */}
              <div className="h-36 flex items-center justify-center relative overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={textIndex}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ 
                      duration: 0.5,
                      ease: "easeOut",
                      opacity: { duration: 0.3 }
                    }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <p className="text-4xl md:text-5xl lg:text-6xl font-light">
                      <span className={`${dynamicTexts[textIndex].color} font-semibold tracking-wide transition-colors duration-300`}>
                        {dynamicTexts[textIndex].text}
                      </span>
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
              
              {/* Brief intro */}
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="max-w-2xl mx-auto text-lg md:text-xl lg:text-2xl text-slate-700 dark:text-slate-300 font-normal leading-relaxed mt-6 mb-8"
              >
               Curious founder solving problems with a passion for tech, an obsession for markets, and unapologetic love for good food.
              </motion.p>
              
              {/* Elegant CTA */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex gap-6 justify-center mt-8"
              >
                <button 
                  onClick={() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' })}
                  className="group relative px-8 py-4 overflow-hidden"
                >
                  <span className="relative z-10 text-gray-900 dark:text-white font-medium">
                    Explore My Projects
                  </span>
                  <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-900 dark:bg-white transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </button>
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors duration-300"
                >
                  Get in Touch
                </button>
              </motion.div>
            </div>
          </div>
          
          {/* Subtle scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <div className="w-5 h-8 border-2 border-gray-300 dark:border-gray-600 rounded-full p-1">
              <div className="w-1 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-scroll" />
            </div>
          </div>
        </motion.section>
      </Element>

      {/* Projects Section - Editorial Grid */}
      <Element name="work">
        <section id="work" className="pt-24 md:pt-32 pb-32 md:pb-40 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Section intro */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-20"
            >
              <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-8">
                <div className="max-w-3xl">
                  <h2 className="text-5xl font-light text-gray-900 dark:text-white mb-6">
                    Projects
                  </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                    A collection of projects I am working on that offers me a challenge to learn and give me a sense of purpose through helping bring people together to have fun.
                  </p>
                </div>
                <Link 
                  to="/projects"
                  className="group inline-flex items-center gap-3 text-lg font-light text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-300 self-start md:mt-4"
                >
                  <span className="relative whitespace-nowrap">
                    View All Projects
                    <span className="absolute -bottom-1 left-0 w-full h-px bg-gradient-to-r from-orange-500 to-amber-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                  </span>
                  <svg 
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </motion.div>
            
            {/* Projects Grid */}
            <div className="space-y-8">
              <Projects preview={true} />
            </div>
          </div>
        </section>
      </Element>

      {/* Articles Section - Minimal Blog */}
      <Element name="writing">
        <section id="writing" className="pt-32 md:pt-40 pb-32 md:pb-48 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-20"
            >
              <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-8">
                <div className="max-w-3xl">
                  <h2 className="text-5xl font-light text-gray-900 dark:text-white mb-6">
                    Latest Thinking
                  </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                    Thoughts on markets, personal development, and creating meaningful digital products.
                  </p>
                </div>
                <Link 
                  to="/blog"
                  className="group inline-flex items-center gap-3 text-lg font-light text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-300 self-start md:mt-4"
                >
                  <span className="relative whitespace-nowrap">
                    Read All Articles
                    <span className="absolute -bottom-1 left-0 w-full h-px bg-gradient-to-r from-orange-500 to-amber-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                  </span>
                  <svg 
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </motion.div>
            
            {/* Articles Grid */}
            <div className="grid md:grid-cols-2 gap-x-16 gap-y-24">
              <Blog preview={true} />
            </div>
          </div>
        </section>
      </Element>

      {/* Contact Section */}
      <Element name="contact">
        <ContactSophisticated />
      </Element>
    </div>
  );
}
